import { roomsApi } from "@/features/room-builder/api";
import { stagingsApi } from "@/features/stagings/api";
import { stagingsKeys } from "@/features/stagings/queryKeys";
import { baseUrl } from "@/lib/http";
import { FURNITURE_STATUS } from "@/types/api";
import type { Furniture, FurnitureItem, Room, RoomGenericItem } from "@/types/api";
import EditNoteIcon from "@mui/icons-material/EditNote";
import GridViewIcon from "@mui/icons-material/GridView";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import PrintIcon from "@mui/icons-material/Print";
import TableRowsIcon from "@mui/icons-material/TableRows";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { stagingViewApi } from "./api";
import { roomKeys, roomsKeys } from "./queryKeys";

// ─── helpers ──────────────────────────────────────────────────────────────────

type LocationFilter = "locker" | "other" | "atLocation";

interface Counts {
  locker: number;
  other: number;
  atLocation: number;
}

function classifyItem(item: FurnitureItem, stagingId: number): LocationFilter {
  // Not currently in any room → sitting in a locker / storage.
  if (!item.roomInfo) return "locker";
  // In a room of the staging we're viewing → at this location.
  if (item.roomInfo.staging === stagingId) return "atLocation";
  // In a room that belongs to a different staging → at another house.
  return "other";
}

function roomLabel(room?: Room | null): string {
  if (!room) return "—";
  const addr = room.stagingInfo?.address ?? "";
  const name = room.name ?? "";
  return addr ? `${addr} (${name})` : name || "—";
}

function locationLabel(item: FurnitureItem): string {
  if (item.roomInfo) return roomLabel(item.roomInfo);
  if (item.locationInfo) {
    const loc = item.locationInfo;
    const cat = loc.categoryInfo?.name ?? "";
    const parts = [cat, loc.bin, loc.slot].filter(Boolean);
    return parts.join(" ") || "—";
  }
  return "Locker";
}

// The item is physically in a room that belongs to a different staging than the
// one it is reserved for — flagged with the same Camel used in the room builder.
function isAtOtherStaging(item: FurnitureItem): boolean {
  return !!(
    item.roomInfo
    && item.reservationInfo
    && item.roomInfo.staging !== item.reservationInfo.staging
  );
}

function locationColor(item: FurnitureItem): string {
  if (isAtOtherStaging(item)) return "warning.dark";
  if (item.roomInfo) return "primary.main";
  return "text.secondary";
}

function dimLabel(info?: Furniture | null): string {
  if (!info) return "";
  const parts: string[] = [];
  if (info.width != null) parts.push(`${info.width}W`);
  if (info.height != null) parts.push(`${info.height}H`);
  if (info.length != null) parts.push(`${info.length}D`);
  return parts.join(" ");
}

function thumb(path?: string | null): string | undefined {
  return path ? `${baseUrl}${path}` : undefined;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function StagingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const stagingId = Number(id);
  const nav = useNavigate();

  const [activeFilters, setActiveFilters] = useState<Set<LocationFilter>>(
    () => new Set<LocationFilter>(["locker", "other", "atLocation"]),
  );
  const [showPlaceholders, setShowPlaceholders] = useState(true);
  const [view, setView] = useState<"list" | "grid">("list");

  const stagingQuery = useQuery({
    queryKey: stagingsKeys.detail(stagingId),
    queryFn: () => stagingsApi.get(stagingId),
    enabled: !Number.isNaN(stagingId),
  });

  const roomsQuery = useQuery({
    queryKey: roomsKeys.list(stagingId),
    queryFn: () => roomsApi.list(stagingId),
    enabled: !Number.isNaN(stagingId),
  });

  const roomDetailQueries = useQueries({
    queries: (roomsQuery.data ?? []).map((room) => ({
      queryKey: roomKeys.detail(room.id),
      queryFn: () => roomsApi.get(room.id),
      enabled: !!roomsQuery.data,
    })),
  });

  const totals = useMemo<Counts>(() => {
    const acc: Counts = { locker: 0, other: 0, atLocation: 0 };
    for (const q of roomDetailQueries) {
      if (!q.data) continue;
      for (const item of q.data.furnitureItemHistory ?? []) {
        acc[classifyItem(item, stagingId)]++;
      }
    }
    return acc;
  }, [roomDetailQueries, stagingId]);

  const toggleFilter = (f: LocationFilter) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(f)) next.delete(f);
      else next.add(f);
      return next;
    });
  };

  if (stagingQuery.isLoading || roomsQuery.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const staging = stagingQuery.data;
  if (!staging) return null;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" sx={{ flex: 1, fontWeight: 700 }}>
          {staging.address}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<MeetingRoomIcon />}
          onClick={() => void nav(`/staging/${stagingId}/room-builder`)}
        >
          Room builder
        </Button>
      </Stack>

      {/* Controls */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>
            Location filter
          </Typography>
          {(
            [
              { key: "locker", label: "Locker" },
              { key: "other", label: "At other houses" },
              { key: "atLocation", label: "At location" },
            ] as const
          ).map(({ key, label }) => (
            <Button
              key={key}
              size="small"
              variant={activeFilters.has(key) ? "contained" : "outlined"}
              color="primary"
              onClick={() => toggleFilter(key)}
              sx={{ textTransform: "uppercase", fontWeight: 700 }}
            >
              {label} ({totals[key]})
            </Button>
          ))}
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={() => void nav(`/staging/${stagingId}/print`)}
          >
            Print
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={view === "grid" ? <TableRowsIcon /> : <GridViewIcon />}
            onClick={() => setView(view === "grid" ? "list" : "grid")}
          >
            {view === "grid" ? "Table" : "Grid"}
          </Button>
        </Stack>
        <FormControlLabel
          control={
            <Checkbox
              checked={showPlaceholders}
              onChange={(e) => setShowPlaceholders(e.target.checked)}
            />
          }
          label="Show placeholders"
          sx={{ mt: 1, display: "block" }}
        />
      </Paper>

      {/* Room sections */}
      {(roomsQuery.data ?? []).map((roomBasic, i) => {
        const detail = roomDetailQueries[i]?.data;
        const loading = roomDetailQueries[i]?.isLoading;
        return (
          <RoomSection
            key={roomBasic.id}
            room={detail ?? roomBasic}
            loading={loading}
            activeFilters={activeFilters}
            showPlaceholders={showPlaceholders}
            view={view}
            stagingId={stagingId}
          />
        );
      })}
    </Box>
  );
}

// ─── Room section ──────────────────────────────────────────────────────────────

interface RoomSectionProps {
  room: Room;
  loading?: boolean;
  activeFilters: Set<LocationFilter>;
  showPlaceholders: boolean;
  view: "list" | "grid";
  stagingId: number;
}

function RoomSection({
  room,
  loading,
  activeFilters,
  showPlaceholders,
  view,
  stagingId,
}: RoomSectionProps) {
  const nav = useNavigate();

  const visibleItems = (room.furnitureItemHistory ?? []).filter(
    (it) => activeFilters.size === 0 || activeFilters.has(classifyItem(it, stagingId)),
  );
  const placeholders = showPlaceholders ? (room.templateAssoc?.items ?? []) : [];

  return (
    <Paper sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", px: 2, pt: 2, pb: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          {room.name}
        </Typography>
        <IconButton
          size="small"
          color="primary"
          onClick={() => void nav(`/staging/${stagingId}/room-builder/${room.id}`)}
        >
          <EditNoteIcon />
        </IconButton>
      </Box>

      {loading
        ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )
        : view === "list"
        ? (
          <ListView
            roomId={room.id}
            items={visibleItems}
            placeholders={placeholders}
          />
        )
        : (
          <GridView
            roomId={room.id}
            items={visibleItems}
            placeholders={placeholders}
          />
        )}
    </Paper>
  );
}

// ─── List view ─────────────────────────────────────────────────────────────────

interface ListViewProps {
  roomId: number;
  items: FurnitureItem[];
  placeholders: RoomGenericItem[];
}

function ListView({ roomId, items, placeholders }: ListViewProps) {
  const empty = items.length === 0 && placeholders.length === 0;
  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: { xs: 720, sm: 0 } }}>
        <TableHead>
          <TableRow>
            {(["IMAGE", "ITEM", "LOCATION", "STATUS", "NOTES"] as const).map((h) => (
              <TableCell
                key={h}
                sx={{
                  color: "text.secondary",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  width: h === "IMAGE"
                    ? 56
                    : h === "LOCATION"
                    ? 150
                    : h === "STATUS"
                    ? 220
                    : undefined,
                }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it) => <ItemRow key={it.id} item={it} roomId={roomId} />)}
          {placeholders.map((ph) => <PlaceholderListRow key={ph.id} item={ph} />)}
          {empty && (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 2, color: "text.secondary" }}>
                No items match the current filter.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ItemRow({ item, roomId }: { item: FurnitureItem; roomId: number }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [status, setStatus] = useState(item.status);
  useEffect(() => setStatus(item.status), [item.status]);

  const mutation = useMutation({
    mutationFn: (newStatus: number) => stagingViewApi.patchItemStatus(item.id, newStatus),
    onSuccess: () => qc.invalidateQueries({ queryKey: roomKeys.detail(roomId) }),
    onError: () => setStatus(item.status),
  });

  const imgSrc = thumb(
    item.furnitureInfo?.imageFile?.thumbnailPath40
      ?? item.furnitureInfo?.imageFile?.thumbnailPath110,
  );

  return (
    <TableRow>
      <TableCell>
        <Box
          sx={{
            width: 40,
            height: 40,
            bgcolor: "grey.200",
            backgroundImage: `url(${imgSrc})`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
          }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2">{item.furnitureInfo?.name ?? "—"}</Typography>
        <Typography variant="caption" color="text.secondary">
          {dimLabel(item.furnitureInfo)}
        </Typography>
      </TableCell>
      <TableCell
        onClick={() =>
          item.roomInfo
          && void nav(`/staging/${item.roomInfo.staging}/room-builder/${item.roomInfo.id}`)}
        sx={{
          cursor: item.roomInfo ? "pointer" : "default",
          "&:hover": item.roomInfo ? { bgcolor: "action.hover" } : undefined,
        }}
      >
        <Typography variant="body2" color={locationColor(item)}>
          {locationLabel(item)}
        </Typography>
      </TableCell>
      <TableCell>
        <TextField
          select
          size="small"
          variant="standard"
          value={status}
          onChange={(e) => {
            const v = Number(e.target.value);
            setStatus(v);
            mutation.mutate(v);
          }}
          disabled={mutation.isPending}
          sx={{ minWidth: 190 }}
        >
          {Object.entries(FURNITURE_STATUS).map(([k, v]) => (
            <MenuItem key={k} value={Number(k)}>
              {v}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell sx={{ color: "text.secondary" }}>
        <Typography variant="body2">{item.roomNotes || item.notes || "—"}</Typography>
      </TableCell>
    </TableRow>
  );
}

function PlaceholderListRow({ item }: { item: RoomGenericItem }) {
  return (
    <TableRow>
      <TableCell>
        <Box
          sx={{
            width: 40,
            height: 40,
            border: "2px dashed",
            borderColor: "grey.400",
            borderRadius: 1,
          }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontStyle="italic" color="text.secondary">
          {item.name}
          {item.quantity > 1 && ` ×${item.quantity}`}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      </TableCell>
      <TableCell />
    </TableRow>
  );
}

// ─── Grid view ─────────────────────────────────────────────────────────────────

interface GridViewProps {
  roomId: number;
  items: FurnitureItem[];
  placeholders: RoomGenericItem[];
}

function GridView({ roomId, items, placeholders }: GridViewProps) {
  return (
    <Box
      sx={{
        p: 2,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
        gap: 2,
      }}
    >
      {items.map((it) => <ItemCard key={it.id} item={it} roomId={roomId} />)}
      {placeholders.map((ph) => <PlaceholderCard key={ph.id} item={ph} />)}
    </Box>
  );
}

function ItemCard({ item, roomId }: { item: FurnitureItem; roomId: number }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [status, setStatus] = useState(item.status);
  const [notes, setNotes] = useState(item.roomNotes || item.notes || "");
  useEffect(() => setStatus(item.status), [item.status]);
  useEffect(() => setNotes(item.roomNotes || item.notes || ""), [item.roomNotes, item.notes]);

  const statusMutation = useMutation({
    mutationFn: (newStatus: number) => stagingViewApi.patchItemStatus(item.id, newStatus),
    onSuccess: () => qc.invalidateQueries({ queryKey: roomKeys.detail(roomId) }),
    onError: () => setStatus(item.status),
  });

  const notesMutation = useMutation({
    mutationFn: (newNotes: string) =>
      stagingViewApi.updateReservation(roomId, item.id, {
        checked: item.roomChecked,
        notes: newNotes,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: roomKeys.detail(roomId) }),
    onError: () => setNotes(item.roomNotes || item.notes || ""),
  });

  const imgSrc = thumb(
    item.furnitureInfo?.imageFile?.thumbnailPath280
      ?? item.furnitureInfo?.imageFile?.thumbnailPath110,
  );

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          width: "100%",
          aspectRatio: "1",
          bgcolor: "grey.100",
          backgroundImage: imgSrc ? `url(${imgSrc})` : undefined,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }}
      />
      <Box sx={{ p: 1.5, flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography variant="body2" fontWeight={600} noWrap title={item.furnitureInfo?.name ?? "—"}>
          {item.furnitureInfo?.name ?? "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {dimLabel(item.furnitureInfo) || " "}
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          Location
        </Typography>
        <Typography
          variant="body2"
          color={locationColor(item)}
          onClick={() =>
            item.roomInfo
            && void nav(`/staging/${item.roomInfo.staging}/room-builder/${item.roomInfo.id}`)}
          sx={{
            fontSize: 12,
            cursor: item.roomInfo ? "pointer" : "default",
            "&:hover": item.roomInfo ? { textDecoration: "underline" } : undefined,
          }}
        >
          {locationLabel(item)}
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          Status
        </Typography>
        <Select
          size="small"
          value={status}
          onChange={(e) => {
            const v = Number(e.target.value);
            setStatus(v);
            statusMutation.mutate(v);
          }}
          disabled={statusMutation.isPending}
          sx={{ fontSize: 12, mt: 0.25 }}
        >
          {Object.entries(FURNITURE_STATUS).map(([k, v]) => (
            <MenuItem key={k} value={Number(k)} sx={{ fontSize: 12 }}>
              {v}
            </MenuItem>
          ))}
        </Select>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          Notes
        </Typography>
        <TextField
          size="small"
          multiline
          rows={2}
          fullWidth
          value={notes}
          onChange={(e) =>
            setNotes(e.target.value.slice(0, 300))}
          onBlur={() => {
            const trimmed = notes.trim();
            const original = (item.roomNotes || item.notes || "").trim();
            if (trimmed !== original) {
              notesMutation.mutate(trimmed);
            }
          }}
          inputProps={{ maxLength: 300 }}
          sx={{ mt: 0.25 }}
          helperText={`${notes.length} / 300`}
          FormHelperTextProps={{ sx: { textAlign: "right", mr: 0 } }}
        />
      </Box>
    </Paper>
  );
}

function PlaceholderCard({ item }: { item: RoomGenericItem }) {
  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Box
        sx={{
          width: "100%",
          aspectRatio: "1",
          border: "2px dashed",
          borderColor: "grey.300",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 1,
        }}
      >
        <Typography variant="caption" color="text.disabled" align="center">
          {item.name}
        </Typography>
      </Box>
      <Box sx={{ p: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography
          variant="body2"
          fontStyle="italic"
          color="text.secondary"
          noWrap
          sx={{ flex: 1, minWidth: 0 }}
        >
          {item.name}
        </Typography>
        {item.quantity > 1 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
            ×{item.quantity}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
